"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { CATATAN, GRATIS, KONTAK_SWAB, PEMERIKSAAN, POSTER } from "./swab";

const LEBAR_HALAMAN = 1140;

const TAUTKAN_INDUK = true;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "Swab & PCR Test", en: "Swab & PCR Test" },
  sub: {
    id: "Pemeriksaan swab dan PCR di RS Jakarta Medical Center. Hubungi customer service untuk reservasi dan informasi jenis pemeriksaan yang tersedia.",
    en: "Swab and PCR testing at RS Jakarta Medical Center. Contact customer service to book and to ask which tests are available.",
  },
  hubungiCs: { id: "Hubungi Customer Service", en: "Contact Customer Service" },
  telepon: { id: "Telepon", en: "Call" },

  mulaiDari: { id: "Mulai dari", en: "From" },
  pemeriksaan: { id: "Pilihan Pemeriksaan", en: "Available Tests" },
  pemeriksaanJml: { id: "tahapan", en: "steps" },
  lihatRincian: { id: "Lihat rincian", en: "See details" },
  rincian: { id: "Rincian Pemeriksaan", en: "What Each Test Covers" },
  belumAda: {
    id: "Rincian pemeriksaan tersedia di customer service.",
    en: "Details are available from customer service.",
  },
  gratis: { id: "Termasuk Tanpa Biaya Tambahan", en: "Included at No Extra Cost" },
  hubungi: { id: "Hubungi kami", en: "Contact us" },

  catatan: { id: "Yang Perlu Diketahui", en: "Good to Know" },

  poster: { id: "Poster Layanan", en: "Service Poster" },
  posterSub: {
    id: "Versi cetak dari daftar di atas. Klik untuk memperbesar.",
    en: "The printed version of the list above. Click to enlarge.",
  },
  tutup: { id: "Tutup", en: "Close" },
  sebelumnya: { id: "Sebelumnya", en: "Previous" },
  berikutnya: { id: "Berikutnya", en: "Next" },

  reservasi: { id: "Reservasi Swab & PCR", en: "Book a Swab or PCR Test" },
  reservasiSub: {
    id: "Customer service kami membantu memilih pemeriksaan dan mengatur jadwal.",
    en: "Our customer service can help you pick a test and arrange a schedule.",
  },
  catatanReservasi: {
    id: "Harga mengikuti daftar di atas. Customer service akan mengonfirmasi sebelum jadwal ditetapkan.",
    en: "Prices follow the list above. Customer service will confirm before the schedule is set.",
  },
  labelJenis: { id: "Jenis pemeriksaan", en: "Test type" },
  totalnya: { id: "Total", en: "Total" },
  akanDikonfirmasi: {
    id: "Akan dikonfirmasi customer service",
    en: "To be confirmed by customer service",
  },
  lanjutWa: { id: "Lanjut ke WhatsApp", en: "Continue on WhatsApp" },
  penutupPesan: {
    id: "Mohon informasi jenis pemeriksaan, biaya, dan jadwal yang tersedia. Terima kasih.",
    en: "Could you let me know the available tests, costs and schedule? Thank you.",
  },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

function rupiah(nilai) {
  if (nilai == null) return null;
  return `Rp ${new Intl.NumberFormat("id-ID").format(nilai)}`;
}

function hargaTerendah() {
  const angka = PEMERIKSAAN.map((p) => p.harga).filter((h) => h != null);
  return angka.length ? Math.min(...angka) : null;
}

export default function SwabPcrContent() {
  const { lang, t } = useLanguage();
  const [pratinjau, setPratinjau] = useState(null);

  const [terbuka, setTerbuka] = useState(PEMERIKSAAN[0]?.kode ?? null);

  const waHref = `https://wa.me/${KONTAK_SWAB.cs.wa}`;
  const termurah = rupiah(hargaTerendah());

  /* Kalau tidak satu pemeriksaan pun punya rincian, seluruh bagian
     akordeon tidak dirender - sama seperti MCU Premarital yang
     posternya cuma memuat harga. */
  const adaRincian = PEMERIKSAAN.some((p) => p.isi?.length > 0);

  function bukaRincian(kode) {
    setTerbuka(kode);
    requestAnimationFrame(() => {
      document.getElementById(`rincian-swab-${kode}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}>
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA ---------- */}
      <div className="bg-[var(--c-tint)]">
        <div style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "48px 16px 44px" }}>
          <p className="font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]" style={{ fontSize: 11.5 }}>
            {pilihTeks(TEKS.eyebrow, lang)}
          </p>

          <h1 className="text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ marginTop: 10, fontSize: "clamp(30px, 4.8vw, 46px)" }}>
            {pilihTeks(TEKS.judul, lang)}
          </h1>

          <p className="text-[var(--c-body)]" style={{ marginTop: 14, maxWidth: 660, fontSize: 15.5, lineHeight: 1.7 }}>
            {pilihTeks(TEKS.sub, lang)}
          </p>

          {termurah && (
            <p style={{ marginTop: 18 }}>
              <span className="text-[var(--c-muted)]" style={{ fontSize: 13 }}>
                {pilihTeks(TEKS.mulaiDari, lang)}{" "}
              </span>
              <span className="font-bold text-[var(--c-ink)]" style={{ fontSize: 22 }}>
                {termurah}
              </span>
            </p>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 22 }}>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-[10px] bg-[var(--c-brand)] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)]"
              style={{ padding: "12px 20px", fontSize: 14.5 }}
            >
              {pilihTeks(TEKS.hubungiCs, lang)}
              <span aria-hidden="true">&rarr;</span>
            </a>

            {/* Nomor teleponnya jadi tautan tel:, bukan sekadar teks -
               di ponsel cukup ditekan sekali. Di halaman lama, nomor
               itu harus disalin manual. */}
            <a
              href={`tel:${KONTAK_SWAB.telepon.tel}`}
              className="inline-flex items-center gap-2 rounded-[10px] border border-[var(--c-brand-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint-2)]"
              style={{ padding: "12px 20px", fontSize: 14.5 }}
            >
              {pilihTeks(TEKS.telepon, lang)} {KONTAK_SWAB.telepon.tampil}
            </a>
          </div>
        </div>
      </div>

      <section style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "44px 16px 64px" }}>

        {/* ---------- SPANDUK MODE PENGEMBANGAN ---------- */}
        {process.env.NODE_ENV !== "production" && PEMERIKSAAN.length === 0 && (
          <div className="border-l-4 border-red-500 bg-red-50" style={{ marginBottom: 40, borderRadius: 8, padding: "14px 16px" }}>
            <p className="font-bold text-red-800" style={{ fontSize: 13.5 }}>
              Hanya tampil saat pengembangan - bukan bagian dari halaman
            </p>
            <ul className="text-red-700" style={{ marginTop: 6, fontSize: 13, listStyle: "disc", paddingLeft: 20 }}>
              <li>jenis pemeriksaan dan harga belum diisi di swab.js</li>
              <li>pastikan layanan swab/PCR memang masih berjalan - posternya dari masa pandemi</li>
            </ul>
          </div>
        )}

        {/* ---------- JENIS PEMERIKSAAN ----------
           Muncul sendiri begitu PEMERIKSAAN di swab.js diisi. */}
        {PEMERIKSAAN.length > 0 && (
          <>
            <h2 className="font-bold leading-tight text-[var(--c-ink)]" style={{ fontSize: 28 }}>
              {pilihTeks(TEKS.pemeriksaan, lang)}
            </h2>

            {/* minmax 330px: sama dengan kartu paket di halaman MCU,
               jadi lebar dan jaraknya seragam di kedua halaman. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 18, marginTop: 22 }}>
              {PEMERIKSAAN.map((p) => (
                <KartuPemeriksaan
                  key={p.kode}
                  paket={p}
                  lang={lang}
                  onRincian={adaRincian ? () => bukaRincian(p.kode) : null}
                />
              ))}
            </div>

            {/* ---------- AKORDEON ----------
               Panel tertutup tetap dirender dengan `hidden`, bukan
               dilepas, supaya Ctrl+F peramban dan mesin pencari tetap
               menemukan isinya. */}
            {adaRincian && (
              <>
                <h3 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 44, fontSize: 19 }}>
                  {pilihTeks(TEKS.rincian, lang)}
                </h3>

                <div style={{ marginTop: 14, borderTop: "1px solid #e2e8f0" }}>
                  {PEMERIKSAAN.map((p) => (
                    <PanelRincian
                      key={p.kode}
                      paket={p}
                      lang={lang}
                      terbuka={terbuka === p.kode}
                      onToggle={() => setTerbuka(terbuka === p.kode ? null : p.kode)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* ---------- GRATIS ----------
               Cakupan tiap butir ditulis sebagai label kecil, bukan
               satu kalimat pukul rata di bawah daftar. */}
            {GRATIS.length > 0 && (
              <>
                <h3 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 44, fontSize: 19 }}>
                  {pilihTeks(TEKS.gratis, lang)}
                </h3>

                <ul style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 16 }}>
                  {GRATIS.map((g, i) => (
                    <li key={i} className="border border-[var(--c-line)] bg-[var(--c-tint)]" style={{ borderRadius: 10, padding: "14px 16px" }}>
                      <p className="font-semibold text-[var(--c-ink)]" style={{ fontSize: 14 }}>
                        {pilihTeks(g.nama, lang)}
                      </p>
                      <p className="font-semibold uppercase tracking-[0.1em] text-[var(--c-brand-mid)]" style={{ marginTop: 6, fontSize: 10.5 }}>
                        {pilihTeks(g.untuk, lang)}
                      </p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        {/* ---------- CATATAN ---------- */}
        {CATATAN.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 22 }}>
              {pilihTeks(TEKS.catatan, lang)}
            </h2>
            <ul style={{ marginTop: 16, maxWidth: 720, display: "grid", gap: 10 }}>
              {CATATAN.map((c, i) => (
                <li key={i} className="text-slate-700" style={{ display: "flex", gap: 10, fontSize: 14.5, lineHeight: 1.7 }}>
                  <span aria-hidden="true" className="font-bold text-[var(--c-brand-mid)]">&bull;</span>
                  <span>{pilihTeks(c, lang)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* ---------- POSTER ---------- */}
        {POSTER.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: PEMERIKSAAN.length > 0 || CATATAN.length > 0 ? 56 : 0, fontSize: 22 }}>
              {pilihTeks(TEKS.poster, lang)}
            </h2>
            <p className="text-[var(--c-muted)]" style={{ marginTop: 8, fontSize: 13.5 }}>
              {pilihTeks(TEKS.posterSub, lang)}
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginTop: 16 }}>
              {POSTER.map((p, i) => (
                <button
                  key={i}
                  id={`poster-swab-${i}`}
                  type="button"
                  onClick={() => setPratinjau(i)}
                  className="group block w-full overflow-hidden border border-[var(--c-line)] text-left transition-colors hover:border-[var(--c-brand-line)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
                  style={{ borderRadius: 12 }}
                >
                  <Image
                    src={p.gambar}
                    alt={pilihTeks(p.alt, lang)}
                    placeholder="blur"
                    sizes="(min-width: 768px) 420px, 100vw"
                    className="h-auto w-full transition-transform duration-500 ease-out group-hover:scale-[1.02] motion-reduce:transform-none"
                  />
                  {p.judul && (
                    <p className="border-t border-[var(--c-line)] font-semibold text-[var(--c-body)]" style={{ padding: "10px 14px", fontSize: 12.5 }}>
                      {pilihTeks(p.judul, lang)}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ---------- RESERVASI ---------- */}
        <Reservasi lang={lang} />
      </section>

      <Footer />

      {pratinjau !== null && (
        <Pratinjau
          indeks={pratinjau}
          lang={lang}
          onTutup={() => setPratinjau(null)}
          onPindah={(i) => setPratinjau(i)}
        />
      )}
    </div>
  );
}

/* ============================================================
   KARTU PEMERIKSAAN

   Bentuk dan ukurannya mengikuti kartu paket di halaman MCU:
   lencana kode, nama, julukan, harga besar, jumlah tahapan, lalu
   tautan rincian. Isi pemeriksaan tidak ditaruh di kartu supaya
   tinggi kartunya tidak timpang dan harganya tidak terdorong ke
   bawah layar.
   ============================================================ */

function KartuPemeriksaan({ paket, lang, onRincian }) {
  const harga = rupiah(paket.harga);

  return (
    <article className="border border-[var(--c-line)] transition-colors hover:border-[var(--c-brand-line)]" style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "20px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Lencana>{paket.kode}</Lencana>
        <div style={{ minWidth: 0 }}>
          <p className="font-bold leading-tight text-[var(--c-ink)]" style={{ fontSize: 16.5 }}>
            {pilihTeks(paket.nama, lang)}
          </p>
          {paket.julukan && (
            <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-brand-mid)]" style={{ marginTop: 3, fontSize: 10.5 }}>
              {pilihTeks(paket.julukan, lang)}
            </p>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16, flex: "1 1 auto" }}>
        {harga ? (
          <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 23 }}>
            {harga}
          </p>
        ) : (
          <p className="font-bold text-[var(--c-brand)]" style={{ fontSize: 18 }}>
            {pilihTeks(TEKS.hubungi, lang)}
          </p>
        )}

        {paket.isi?.length > 0 && (
          <p className="text-[var(--c-muted)]" style={{ marginTop: 6, fontSize: 13 }}>
            {paket.isi.length} {pilihTeks(TEKS.pemeriksaanJml, lang)}
          </p>
        )}

        {paket.catatan && (
          <p className="text-[var(--c-muted)]" style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.55 }}>
            {pilihTeks(paket.catatan, lang)}
          </p>
        )}
      </div>

      {/* Tombolnya hilang kalau memang tidak ada rincian untuk dituju -
         tombol yang tidak membawa ke mana-mana lebih buruk daripada
         tidak ada tombol. */}
      {onRincian && (
        <button
          type="button"
          onClick={onRincian}
          className="font-bold text-[var(--c-brand)] underline underline-offset-4 transition-colors hover:text-[var(--c-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
          style={{ marginTop: 18, fontSize: 13.5, textAlign: "left" }}
        >
          {pilihTeks(TEKS.lihatRincian, lang)}
        </button>
      )}
    </article>
  );
}

/* ============================================================
   PANEL RINCIAN

   Daftar dipecah jadi beberapa kolom lewat CSS columns berlebar
   minimum 230px - kolomnya bertambah sendiri di layar lebar dan
   menyusut jadi satu di ponsel, tanpa media query.
   ============================================================ */

function PanelRincian({ paket, lang, terbuka, onToggle }) {
  const idPanel = `rincian-swab-${paket.kode}`;

  return (
    <div id={idPanel} style={{ borderBottom: "1px solid #e2e8f0", scrollMarginTop: 120 }}>
      <h4>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={terbuka}
          aria-controls={`${idPanel}-isi`}
          className="w-full text-left transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand)]"
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 8px" }}
        >
          <Lencana aktif={terbuka}>{paket.kode}</Lencana>

          <span className="font-bold text-[var(--c-ink)]" style={{ fontSize: 15.5, flex: "1 1 auto", minWidth: 0 }}>
            {pilihTeks(paket.nama, lang)}
            {paket.julukan && (
              <span className="font-semibold text-[var(--c-faint)]"> &middot; {pilihTeks(paket.julukan, lang)}</span>
            )}
          </span>

          <span className="text-[var(--c-muted)]" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
            {paket.isi?.length > 0 ? `${paket.isi.length} ${pilihTeks(TEKS.pemeriksaanJml, lang)}` : ""}
          </span>

          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0 text-[var(--c-brand)] transition-transform duration-300 motion-reduce:transition-none" style={{ width: 18, height: 18, transform: terbuka ? "rotate(180deg)" : "none" }}>
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </h4>

      <div id={`${idPanel}-isi`} hidden={!terbuka} style={{ padding: "0 8px 22px 8px" }}>
        {!paket.isi?.length ? (
          <p className="text-[var(--c-muted)]" style={{ fontSize: 14 }}>
            {pilihTeks(TEKS.belumAda, lang)}
          </p>
        ) : (
          <ol className="text-slate-700" style={{ columns: "230px", columnGap: 32, listStyle: "decimal", paddingLeft: 22, margin: 0 }}>
            {paket.isi.map((butir, i) => (
              <li key={i} style={{ breakInside: "avoid", marginBottom: 7, fontSize: 14, lineHeight: 1.55 }}>
                {pilihTeks(butir, lang)}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

/* Lencana berbentuk pil, bukan lingkaran berukuran tetap: kodenya
   bisa "PCR" atau "Antigen". Sama dengan halaman MCU. */
function Lencana({ children, aktif = true }) {
  return (
    <span
      className="font-bold text-white"
      style={{
        backgroundColor: aktif ? "#0f6a3b" : "#8fc9a9",
        borderRadius: 8,
        padding: "5px 10px",
        fontSize: 12.5,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        flex: "0 0 auto",
      }}
    >
      {children}
    </span>
  );
}

/* ============================================================
   RESERVASI

   SENGAJA RINGKAS: hanya pilihan pemeriksaan dan total.

   Kolom tanggal, jam, nama pasien, jumlah, dan catatan pernah ada di
   sini lalu dilepas atas permintaan pemilik situs, supaya bentuknya
   sama dengan kotak reservasi di halaman MCU.

   Konsekuensinya: pesan yang sampai ke customer service hanya berisi
   jenis pemeriksaan dan harganya. Tanggal, nama pasien, dan jumlah
   peserta harus ditanyakan lewat percakapan - atau diketik sendiri
   oleh pengunjung, karena WhatsApp hanya MENGISI kolom ketik dan
   pesannya masih bisa disunting sebelum dikirim.

   KALAU NANTI KOLOM-KOLOM ITU MAU DIKEMBALIKAN, bentuknya masih ada
   di halaman MCU (app/layanan/mcu/MCUContent.jsx) beserta JADWAL di
   mcu.js - tinggal disalin. JADWAL di swab.js sengaja dibiarkan
   untuk keperluan itu, walau saat ini tidak dipakai.
   ============================================================ */

function Reservasi({ lang }) {
  /* Pilihan pertama langsung terpilih, sama seperti dropdown paket di
     halaman MCU. Dengan begitu totalnya sudah terlihat begitu kotak
     ini dibuka - tidak menunggu pengunjung memilih dulu, yang pada
     halaman berisi satu pemeriksaan saja hanya jadi langkah kosong. */
  const [kodeJenis, setKodeJenis] = useState(PEMERIKSAAN[0]?.kode ?? "");

  const jenis = PEMERIKSAAN.find((p) => p.kode === kodeJenis) ?? PEMERIKSAAN[0] ?? null;
  const total = jenis?.harga ?? null;
  const en = lang === "en";

  /* Kalau totalnya belum bisa ditampilkan, pasien perlu ikut
     MENANYAKAN harganya - bukan cuma jadwalnya. Tanpa ini, pesannya
     hanya menanyakan jadwal padahal yang belum dia ketahui justru
     harganya, dan customer service harus menebak. */
  const penutup = rupiah(total)
    ? en
      ? "Could you let me know the available schedule? Thank you."
      : "Mohon informasi jadwal yang tersedia. Terima kasih."
    : pilihTeks(TEKS.penutupPesan, lang);

  const baris = [
    jenis ? `${pilihTeks(TEKS.labelJenis, lang)}: ${pilihTeks(jenis.nama, lang)}` : "",
    `${pilihTeks(TEKS.totalnya, lang)}: ${rupiah(total) ?? pilihTeks(TEKS.akanDikonfirmasi, lang)}`,
  ].filter(Boolean);

  const pembuka = en
    ? "Hello RS JMC, I would like to book a swab / PCR test."
    : "Halo RS JMC, saya ingin reservasi pemeriksaan swab / PCR.";

  /* WhatsApp hanya mengisi kolom ketik, tidak mengirim - pesannya
     masih bisa disunting sebelum dikirim. Di situ pula pengunjung
     bisa menambahkan sendiri tanggal, nama, atau jumlah peserta,
     karena formulir ini sengaja tidak menanyakannya. */
  const pesan = `${pembuka}\n\n${baris.join("\n")}\n\n${penutup}`;
  const href = `https://wa.me/${KONTAK_SWAB.cs.wa}?text=${encodeURIComponent(pesan)}`;

  return (
    <div id="reservasi" className="bg-[var(--c-tint)]" style={{ marginTop: 56, borderRadius: 14, padding: "28px 24px", scrollMarginTop: 120 }}>
      <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 18 }}>
        {pilihTeks(TEKS.reservasi, lang)}
      </p>
      <p className="text-[var(--c-body)]" style={{ marginTop: 6, maxWidth: 520, fontSize: 14.5, lineHeight: 1.65 }}>
        {pilihTeks(TEKS.reservasiSub, lang)}
      </p>

      {/* Pilihan jenis muncul sendiri begitu PEMERIKSAAN di swab.js
         diisi - sekarang masih kosong, jadi belum dirender.

         <select> bawaan, bukan menu buatan sendiri: di ponsel ia
         memunculkan pemilih bawaan sistem, dan papan ketik serta
         pembaca layar sudah bekerja tanpa ditambah apa pun. */}
      {PEMERIKSAAN.length > 0 && (
        <div style={{ marginTop: 20, maxWidth: 400 }}>
          <label htmlFor="jenis-swab" className="font-semibold text-slate-700" style={{ fontSize: 13 }}>
            {pilihTeks(TEKS.labelJenis, lang)}
          </label>
          <select
            id="jenis-swab"
            value={kodeJenis}
            onChange={(e) => setKodeJenis(e.target.value)}
            className="text-[var(--c-ink)]"
            style={{ width: "100%", marginTop: 6, borderRadius: 10, border: "1px solid #e2e8f0", backgroundColor: "#ffffff", padding: "11px 12px", fontSize: 14.5, outline: "none" }}
          >
            {PEMERIKSAAN.map((p) => (
              <option key={p.kode} value={p.kode}>
                {pilihTeks(p.nama, lang)}
                {p.julukan ? ` - ${pilihTeks(p.julukan, lang)}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* aria-live: pembaca layar mengumumkan totalnya saat berubah.
         Tanpa itu, pengguna mengganti pilihan dan tidak pernah tahu
         angkanya ikut berubah. */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 24 }}>
        <p aria-live="polite" style={{ minWidth: 0 }}>
          <span className="text-[var(--c-muted)]" style={{ fontSize: 13 }}>
            {pilihTeks(TEKS.totalnya, lang)}{" "}
          </span>
          {rupiah(total) ? (
            <span className="font-bold text-[var(--c-ink)]" style={{ fontSize: 26 }}>
              {rupiah(total)}
            </span>
          ) : (
            <span className="font-bold text-[var(--c-brand)]" style={{ fontSize: 16 }}>
              {pilihTeks(TEKS.akanDikonfirmasi, lang)}
            </span>
          )}
        </p>

        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-[10px] bg-[var(--c-brand)] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)]"
          style={{ padding: "12px 20px", fontSize: 14.5 }}
        >
          {pilihTeks(TEKS.lanjutWa, lang)}
          <span aria-hidden="true">&rarr;</span>
        </a>
      </div>

      <p className="text-[var(--c-muted)]" style={{ marginTop: 14, maxWidth: 560, fontSize: 12.5, lineHeight: 1.6 }}>
        {pilihTeks(TEKS.catatanReservasi, lang)}
      </p>
    </div>
  );
}

/* ============================================================
   PRATINJAU POSTER - sama polanya dengan halaman MCU

   Ditutup dengan tombol silang, Esc, atau menekan latarnya. Gulir
   halaman dikunci selama terbuka, dan fokus papan ketik dikembalikan
   ke poster yang ditekan saat ditutup.
   ============================================================ */

function Pratinjau({ indeks, lang, onTutup, onPindah }) {
  const tombolTutup = useRef(null);
  const poster = POSTER[indeks];
  const banyak = POSTER.length > 1;

  useEffect(() => {
    tombolTutup.current?.focus();

    const gulirSemula = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const tekan = (e) => {
      if (e.key === "Escape") onTutup();
      if (!banyak) return;
      if (e.key === "ArrowRight") onPindah((indeks + 1) % POSTER.length);
      if (e.key === "ArrowLeft") onPindah((indeks - 1 + POSTER.length) % POSTER.length);
    };

    window.addEventListener("keydown", tekan);

    return () => {
      window.removeEventListener("keydown", tekan);
      document.body.style.overflow = gulirSemula;
      document.getElementById(`poster-swab-${indeks}`)?.focus();
    };
  }, [indeks, banyak, onTutup, onPindah]);

  if (!poster) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={pilihTeks(poster.judul, lang) || pilihTeks(TEKS.poster, lang)}
      onClick={onTutup}
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(15, 4, 12, 0.86)", padding: 16 }}
    >
      {/* width: fit-content - kotaknya menyusut mengikuti gambar,
         jadi tidak ada bidang kosong di kiri-kanan. */}
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "fit-content", maxWidth: "100%" }}>
        <Image
          src={poster.gambar}
          alt={pilihTeks(poster.alt, lang)}
          sizes="90vw"
          className="rounded-lg"
          style={{ display: "block", width: "auto", height: "auto", maxWidth: "100%", maxHeight: "82vh" }}
        />

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 12 }}>
          <p className="font-semibold text-white/80" style={{ fontSize: 13 }}>
            {pilihTeks(poster.judul, lang)}
          </p>

          {banyak && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="text-white/60" style={{ fontSize: 12.5 }}>
                {indeks + 1} / {POSTER.length}
              </span>
              <button
                type="button"
                onClick={() => onPindah((indeks - 1 + POSTER.length) % POSTER.length)}
                aria-label={pilihTeks(TEKS.sebelumnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &larr;
              </button>
              <button
                type="button"
                onClick={() => onPindah((indeks + 1) % POSTER.length)}
                aria-label={pilihTeks(TEKS.berikutnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &rarr;
              </button>
            </div>
          )}
        </div>

        <button
          ref={tombolTutup}
          type="button"
          onClick={onTutup}
          aria-label={pilihTeks(TEKS.tutup, lang)}
          className="bg-[var(--c-surface)] text-[var(--c-ink)] shadow-lg transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          style={{ position: "absolute", top: -14, right: -6, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 999 }}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18 }}>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
